import React, { useEffect, useState } from 'react';
import { reportsApi, matchesApi, clientsApi } from '../../lib/api';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  RefreshCw, Download, FileText, TrendingUp, TrendingDown, 
  Trophy, Users, DollarSign, Target
} from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';

export function NetResultPage() {
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState('all');
  const [netResult, setNetResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMatches = async () => {
    try {
      const data = await matchesApi.list();
      setMatches(data);
    } catch (error) {
      console.error('Failed to load matches', error);
    }
  };

  const fetchNetResult = async () => {
    try {
      setLoading(true);
      const matchId = selectedMatch === 'all' ? null : selectedMatch;
      const data = await reportsApi.netResult(matchId);
      setNetResult(data);
    } catch (error) {
      toast.error('Failed to load net result');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatches();
  }, []);

  useEffect(() => {
    fetchNetResult();
  }, [selectedMatch]);

  // Calculate summary stats
  const calculateSummary = () => {
    if (!netResult?.results) return null;
    
    const results = netResult.results;
    let totalProfit = 0;
    let totalLoss = 0;
    let totalClients = results.length;
    let profitableClients = 0;
    let losingClients = 0;

    results.forEach(client => {
      const netAmount = Math.min(client.team_a_total, client.team_b_total);
      if (netAmount > 0) {
        totalProfit += netAmount;
        profitableClients++;
      } else if (netAmount < 0) {
        totalLoss += Math.abs(netAmount);
        losingClients++;
      }
    });

    return {
      totalClients,
      profitableClients,
      losingClients,
      totalProfit,
      totalLoss,
      netPL: totalProfit - totalLoss
    };
  };

  const summary = calculateSummary();

  // Export to Server PDF
  const exportServerPDF = async () => {
    if (!netResult?.results) return;

    try {
      const matchId = selectedMatch === 'all' ? null : selectedMatch;
      const response = await reportsApi.exportNetResultPDF(matchId);
      
      // Create blob and download
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `net_result_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Server PDF exported');
    } catch (error) {
      toast.error('Failed to export server PDF');
      console.error(error);
    }
  };

  // Export to PDF
  const exportPDF = () => {
    if (!netResult?.results) return;

    const doc = new jsPDF('p', 'mm', 'a4');
    let yPos = 15;

    // Header
    doc.setFontSize(18);
    doc.setTextColor(212, 175, 55);
    doc.text('Net Result Report', 14, yPos);
    yPos += 8;

    // Subtitle
    doc.setFontSize(10);
    doc.setTextColor(100);
    const matchName = selectedMatch === 'all' 
      ? 'All Matches' 
      : matches.find(m => m.match_id === selectedMatch)?.name || 'Selected Match';
    doc.text(`Match: ${matchName}`, 14, yPos);
    yPos += 5;
    doc.text(`Generated: ${format(new Date(), 'PPpp')}`, 14, yPos);
    yPos += 10;

    // Summary
    if (summary) {
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('Summary', 14, yPos);
      yPos += 6;

      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: [
          ['Total Clients', summary.totalClients],
          ['Profitable Clients', summary.profitableClients],
          ['Losing Clients', summary.losingClients],
          ['Total Profit', `₹${summary.totalProfit.toLocaleString()}`],
          ['Total Loss', `₹${summary.totalLoss.toLocaleString()}`],
          ['Net P/L', `₹${summary.netPL.toLocaleString()}`]
        ],
        theme: 'grid',
        headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] },
        styles: { fontSize: 9 }
      });

      yPos = doc.lastAutoTable.finalY + 10;
    }

    // Client Details
    doc.setFontSize(12);
    doc.text('Client-wise Results', 14, yPos);
    yPos += 6;

    const clientData = netResult.results.map(client => {
      const netAmount = Math.min(client.team_a_total, client.team_b_total);
      return [
        client.client_name,
        `₹${client.team_a_total.toLocaleString()}`,
        `₹${client.team_b_total.toLocaleString()}`,
        `₹${netAmount.toLocaleString()}`,
        netAmount >= 0 ? 'PROFIT' : 'LOSS'
      ];
    });

    autoTable(doc, {
      startY: yPos,
      head: [['Client', 'Team A', 'Team B', 'Net Result', 'Status']],
      body: clientData,
      theme: 'grid',
      headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] },
      styles: { fontSize: 8 },
      columnStyles: {
        4: { 
          cellWidth: 20,
          fontStyle: 'bold'
        }
      },
      didParseCell: (data) => {
        if (data.column.index === 4 && data.section === 'body') {
          data.cell.styles.textColor = data.cell.raw === 'PROFIT' ? [34, 197, 94] : [239, 68, 68];
        }
      }
    });

    doc.save(`net_result_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);
    toast.success('PDF exported');
  };

  // Export to Excel
  const exportExcel = () => {
    if (!netResult?.results) return;

    const data = netResult.results.map(client => {
      const netAmount = Math.min(client.team_a_total, client.team_b_total);
      return {
        'Client Name': client.client_name,
        'Team A Position': client.team_a_total,
        'Team B Position': client.team_b_total,
        'Net Result': netAmount,
        'Status': netAmount >= 0 ? 'PROFIT' : 'LOSS',
        'Matches Count': client.matches?.length || 0
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Net Result');
    XLSX.writeFile(wb, `net_result_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
    toast.success('Excel exported');
  };

  // Print report
  const printReport = () => {
    if (!netResult?.results) return;

    const matchName = selectedMatch === 'all' 
      ? 'All Matches' 
      : matches.find(m => m.match_id === selectedMatch)?.name || 'Selected Match';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Net Result Report - ${matchName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; background: #fff; color: #000; }
          h1 { color: #DAA520; border-bottom: 2px solid #DAA520; padding-bottom: 10px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 30px; }
          .stat-card { padding: 15px; background: #f5f5f5; border-radius: 8px; text-align: center; }
          .stat-value { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
          .stat-label { font-size: 12px; color: #666; }
          .profit { color: #22c55e; }
          .loss { color: #ef4444; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background-color: #DAA520; color: #000; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .text-right { text-align: right; }
          @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <h1>Net Result Report</h1>
        <div class="header">
          <div>Match: <strong>${matchName}</strong></div>
          <div>Generated: ${new Date().toLocaleString()}</div>
        </div>
        
        ${summary ? `
        <div class="summary">
          <div class="stat-card">
            <div class="stat-value">${summary.totalClients}</div>
            <div class="stat-label">Total Clients</div>
          </div>
          <div class="stat-card">
            <div class="stat-value profit">₹${summary.totalProfit.toLocaleString()}</div>
            <div class="stat-label">Total Profit</div>
          </div>
          <div class="stat-card">
            <div class="stat-value loss">₹${summary.totalLoss.toLocaleString()}</div>
            <div class="stat-label">Total Loss</div>
          </div>
        </div>
        ` : ''}
        
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Client Name</th>
              <th class="text-right">Team A</th>
              <th class="text-right">Team B</th>
              <th class="text-right">Net Result</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${netResult.results.map((client, idx) => {
              const netAmount = Math.min(client.team_a_total, client.team_b_total);
              return `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${client.client_name}</td>
                  <td class="text-right">₹${client.team_a_total.toLocaleString()}</td>
                  <td class="text-right">₹${client.team_b_total.toLocaleString()}</td>
                  <td class="text-right ${netAmount >= 0 ? 'profit' : 'loss'}">
                    ₹${netAmount.toLocaleString()}
                  </td>
                  <td class="${netAmount >= 0 ? 'profit' : 'loss'}">
                    ${netAmount >= 0 ? 'PROFIT' : 'LOSS'}
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <script>window.onload = () => window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6 p-4" data-testid="net-result-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-yellow-500">Net Result Report</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchNetResult} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportExcel} disabled={!netResult?.results?.length}>
            <Download className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button variant="outline" size="sm" onClick={exportPDF} disabled={!netResult?.results?.length}>
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={exportServerPDF} disabled={!netResult?.results?.length}>
            <FileText className="w-4 h-4 mr-2" />
            Server PDF
          </Button>
          <Button variant="default" size="sm" onClick={printReport} disabled={!netResult?.results?.length}>
            Print
          </Button>
        </div>
      </div>

      {/* Filter */}
      <GroupBox title="Filter" className="border-gray-700">
        <div className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-xs">
              <label className="text-sm text-gray-400 mb-1 block">Match</label>
              <Select value={selectedMatch} onValueChange={setSelectedMatch}>
                <SelectTrigger>
                  <SelectValue placeholder="Select match" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Matches</SelectItem>
                  {matches.map(match => (
                    <SelectItem key={match.match_id} value={match.match_id}>
                      {match.name} ({match.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </GroupBox>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="p-4 rounded-lg border border-gray-700 bg-gray-800/50">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-yellow-500" />
              <span className="text-sm text-gray-400">Total Clients</span>
            </div>
            <div className="text-2xl font-bold">{summary.totalClients}</div>
          </div>
          
          <div className="p-4 rounded-lg border border-green-500/30 bg-green-900/10">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-400">Profitable</span>
            </div>
            <div className="text-2xl font-bold text-green-500">{summary.profitableClients}</div>
          </div>
          
          <div className="p-4 rounded-lg border border-red-500/30 bg-red-900/10">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-500" />
              <span className="text-sm text-gray-400">Losing</span>
            </div>
            <div className="text-2xl font-bold text-red-500">{summary.losingClients}</div>
          </div>
          
          <div className="p-4 rounded-lg border border-green-500/30 bg-green-900/10">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-400">Total Profit</span>
            </div>
            <div className="text-xl font-bold text-green-500">₹{summary.totalProfit.toLocaleString()}</div>
          </div>
          
          <div className="p-4 rounded-lg border border-red-500/30 bg-red-900/10">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-red-500" />
              <span className="text-sm text-gray-400">Total Loss</span>
            </div>
            <div className="text-xl font-bold text-red-500">₹{summary.totalLoss.toLocaleString()}</div>
          </div>
          
          <div className={`p-4 rounded-lg border ${summary.netPL >= 0 ? 'border-green-500 bg-green-900/20' : 'border-red-500 bg-red-900/20'}`}>
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-yellow-500" />
              <span className="text-sm text-gray-400">Net P/L</span>
            </div>
            <div className={`text-xl font-bold ${summary.netPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              ₹{summary.netPL.toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {/* Results Table */}
      <GroupBox title={`Client Results (${netResult?.results?.length || 0})`} className="border-yellow-500/50">
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-yellow-500" />
            </div>
          ) : netResult?.results?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No results found for the selected match.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4">S.No</th>
                    <th className="text-left py-3 px-4">Client Name</th>
                    <th className="text-right py-3 px-4">Team A Position</th>
                    <th className="text-right py-3 px-4">Team B Position</th>
                    <th className="text-right py-3 px-4">Net Result</th>
                    <th className="text-center py-3 px-4">Status</th>
                    <th className="text-center py-3 px-4">Matches</th>
                  </tr>
                </thead>
                <tbody>
                  {netResult?.results?.map((client, idx) => {
                    const netAmount = Math.min(client.team_a_total, client.team_b_total);
                    const isProfit = netAmount >= 0;
                    return (
                      <tr key={client.client_id} className="border-b border-gray-800 hover:bg-gray-800/50">
                        <td className="py-3 px-4 text-gray-400">{idx + 1}</td>
                        <td className="py-3 px-4 font-medium">{client.client_name}</td>
                        <td className="py-3 px-4 text-right font-mono">
                          ₹{client.team_a_total.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-right font-mono">
                          ₹{client.team_b_total.toLocaleString()}
                        </td>
                        <td className={`py-3 px-4 text-right font-mono font-semibold ${isProfit ? 'text-green-500' : 'text-red-500'}`}>
                          ₹{netAmount.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            isProfit ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                          }`}>
                            {isProfit ? 'PROFIT' : 'LOSS'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-gray-400">
                          {client.matches?.length || 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {netResult?.results?.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-yellow-500/50 bg-gray-800/50 font-semibold">
                      <td className="py-3 px-4" colSpan="2">TOTAL</td>
                      <td className="py-3 px-4 text-right font-mono">
                        ₹{netResult.results.reduce((sum, c) => sum + c.team_a_total, 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-mono">
                        ₹{netResult.results.reduce((sum, c) => sum + c.team_b_total, 0).toLocaleString()}
                      </td>
                      <td className={`py-3 px-4 text-right font-mono ${summary?.netPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        ₹{summary?.netPL?.toLocaleString() || 0}
                      </td>
                      <td className="py-3 px-4 text-center">-</td>
                      <td className="py-3 px-4 text-center text-gray-400">
                        {netResult.results.reduce((sum, c) => sum + (c.matches?.length || 0), 0)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </GroupBox>
    </div>
  );
}

export default NetResultPage;
